import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { apiRequest } from '../lib/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiCreditCard, FiAlertTriangle, FiCheck, FiLoader } = FiIcons;

const StripeSettingsModal = ({ onClose }) => {
  const [formData, setFormData] = useState({ publicKey: '', secretKey: '' });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      const configDoc = await getDoc(doc(db, 'paymentConfig', 'stripe'));
      if (configDoc.exists()) setFormData(configDoc.data());
    };
    load();
  }, []);

  const handleTestConnection = async () => {
    if (!formData.secretKey) return alert("Secret Key required.");
    setTesting(true);
    setTestResult(null);
    try {
      // 1. Save keys to Firestore first so the Cloud Function can read them
      await setDoc(doc(db, 'paymentConfig', 'stripe'), {
        ...formData,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // 2. Call Cloud Function to verify
      const result = await apiRequest('/test-stripe-connection');
      setTestResult({ success: true, message: result.message });
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setDoc(doc(db, 'paymentConfig', 'stripe'), {
        ...formData,
        type: 'stripe',
        updatedAt: new Date().toISOString()
      });
      alert('Stripe configuration saved!');
      onClose();
    } catch (error) {
      alert('Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="flex items-center justify-between p-8 border-b">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Stripe Gateway</h2>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">Live Cloud Configuration</p>
          </div>
          <button onClick={onClose} className="p-4 text-gray-400 hover:bg-gray-50 rounded-2xl"><SafeIcon icon={FiX} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Publishable Key (pk_...)</label>
              <input type="text" value={formData.publicKey} onChange={(e) => setFormData({ ...formData, publicKey: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" placeholder="pk_test_..." required />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Secret Key (sk_...)</label>
              <input type="password" value={formData.secretKey} onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" placeholder="sk_test_..." required />
            </div>
          </div>

          {testResult && (
            <div className={`p-4 rounded-2xl text-xs font-bold flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
              <SafeIcon icon={testResult.success ? FiCheck : FiAlertTriangle} /> {testResult.message}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-6 border-t">
            <button type="button" onClick={handleTestConnection} disabled={testing} className="px-6 py-4 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 rounded-2xl flex items-center gap-2">
              {testing ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Test Cloud Connection'}
            </button>
            <button type="submit" disabled={loading} className="px-10 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700">
              {loading ? 'Saving...' : 'Save & Active'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default StripeSettingsModal;